import React, { useState } from "react";
import axios from "axios";
import { Loader2, CheckCircle2, AlertCircle, MapPin } from "lucide-react";
import { SERVICE_LOCATIONS } from "@/config/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  { value: "repair", label: "Bike Repair" },
  { value: "fitting", label: "Bike Fitting" },
  { value: "custom_build", label: "Custom Build" },
  { value: "custom_class", label: "Custom Class" },
  { value: "other", label: "Something Else" },
];

export default function BookingForm({ defaultService = "repair" }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service_type: defaultService,
    location: "",
    bike_info: "",
    preferred_date: "",
    notes: "",
  });
  const [status, setStatus] = useState({ loading: false, success: false, error: "" });

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ loading: true, success: false, error: "" });
    try {
      await axios.post(`${API}/bookings`, form);
      setStatus({ loading: false, success: true, error: "" });
      setForm({
        name: "",
        email: "",
        phone: "",
        service_type: defaultService,
        location: "",
        bike_info: "",
        preferred_date: "",
        notes: "",
      });
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      setStatus({ loading: false, success: false, error: String(msg) });
    }
  };

  const inputCls =
    "w-full rounded-xl border-2 border-stone-200 bg-stone-50 px-4 py-3 text-base text-leaf-900 placeholder:text-stone-400 focus:border-leaf-500 focus:bg-white focus:outline-none focus:ring-4 focus:ring-leaf-200 transition";

  return (
    <form
      data-testid="booking-form"
      onSubmit={handleSubmit}
      className="space-y-4"
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="bf-name" className="mb-1.5 block text-sm font-semibold text-leaf-900">
            Your Name *
          </label>
          <input
            id="bf-name"
            data-testid="booking-name"
            required
            name="name"
            value={form.name}
            onChange={handleChange}
            placeholder="Alex Rider"
            className={inputCls}
          />
        </div>
        <div>
          <label htmlFor="bf-email" className="mb-1.5 block text-sm font-semibold text-leaf-900">
            Email *
          </label>
          <input
            id="bf-email"
            data-testid="booking-email"
            required
            type="email"
            name="email"
            value={form.email}
            onChange={handleChange}
            placeholder="you@email.com"
            className={inputCls}
          />
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="bf-phone" className="mb-1.5 block text-sm font-semibold text-leaf-900">
            Phone
          </label>
          <input
            id="bf-phone"
            data-testid="booking-phone"
            name="phone"
            value={form.phone}
            onChange={handleChange}
            placeholder="(XXX) XXX-XXXX"
            className={inputCls}
          />
        </div>
        <div>
          <label htmlFor="bf-service" className="mb-1.5 block text-sm font-semibold text-leaf-900">
            Service Type *
          </label>
          <select
            id="bf-service"
            data-testid="booking-service"
            required
            name="service_type"
            value={form.service_type}
            onChange={handleChange}
            className={inputCls}
          >
            {SERVICE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label htmlFor="bf-location" className="mb-1.5 block text-sm font-semibold text-leaf-900">
          Where should we work on it? *
        </label>
        <select
          id="bf-location"
          data-testid="booking-location"
          required
          name="location"
          value={form.location}
          onChange={handleChange}
          className={inputCls}
        >
          <option value="" disabled>
            Choose a location
          </option>
          {SERVICE_LOCATIONS.map((l) => (
            <option key={l.value} value={l.value}>
              {l.label}
            </option>
          ))}
        </select>
        {form.location === "at_home" && (
          <p
            data-testid="booking-location-note"
            className="mt-2 flex items-center gap-1.5 rounded-xl bg-sun-100 px-3 py-2 text-sm font-semibold text-leaf-800"
          >
            <MapPin className="h-4 w-4 shrink-0 text-tomato-500" />
            Heads up: mobile / at-home service has a $50 minimum.
          </p>
        )}
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="bf-bike" className="mb-1.5 block text-sm font-semibold text-leaf-900">
            Bike Info
          </label>
          <input
            id="bf-bike"
            data-testid="booking-bike"
            name="bike_info"
            value={form.bike_info}
            onChange={handleChange}
            placeholder="Make, model, year (optional)"
            className={inputCls}
          />
        </div>
        <div>
          <label htmlFor="bf-date" className="mb-1.5 block text-sm font-semibold text-leaf-900">
            Preferred Date
          </label>
          <input
            id="bf-date"
            data-testid="booking-date"
            type="date"
            name="preferred_date"
            value={form.preferred_date}
            onChange={handleChange}
            className={inputCls}
          />
        </div>
      </div>

      <div>
        <label htmlFor="bf-notes" className="mb-1.5 block text-sm font-semibold text-leaf-900">
          What's going on? / Notes
        </label>
        <textarea
          id="bf-notes"
          data-testid="booking-notes"
          name="notes"
          value={form.notes}
          onChange={handleChange}
          rows={4}
          placeholder="Tell us about your bike and what you need."
          className={inputCls}
        />
      </div>

      {status.error && (
        <div
          data-testid="booking-error"
          className="flex items-start gap-2 rounded-xl bg-clay-500/10 px-4 py-3 text-clay-600"
        >
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
          <span className="text-sm">{status.error}</span>
        </div>
      )}

      {status.success && (
        <div
          data-testid="booking-success"
          className="flex items-start gap-2 rounded-xl bg-leaf-100 px-4 py-3 text-leaf-800"
        >
          <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" />
          <span className="text-sm font-semibold">
            Request received! We'll reach out to confirm your appointment soon.
          </span>
        </div>
      )}

      <button
        type="submit"
        data-testid="booking-submit"
        disabled={status.loading}
        className="group flex w-full items-center justify-center gap-2 rounded-full bg-leaf-600 px-6 py-4 text-base font-bold text-white transition-all hover:-translate-y-0.5 hover:bg-leaf-700 hover:shadow-lg disabled:opacity-60 sm:w-auto sm:px-10"
      >
        {status.loading ? (
          <>
            <Loader2 className="h-5 w-5 animate-spin" />
            Sending…
          </>
        ) : (
          "Request Appointment"
        )}
      </button>
    </form>
  );
}
